import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { intakeQuestionnaireActions } from '../../../../../../../store';
import { hasChildFieldValue, hasChildScheduleErrors, isChildBackgroundComplete } from '../../../../../../common';

import type { AppDispatch, RootState } from '../../../../../../../store';

export default function useIntakeChildBehaviorIssues() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { intakeQuestionnaireId } = useParams<'intakeQuestionnaireId'>();
  const childInfo = useSelector(
    (state: RootState) => state.intakeQuestionnaire.intakeQuestionnaireDetails?.child_info,
  );
  const notAttendingSchool = useSelector(
    (state: RootState) =>
      state.intakeQuestionnaire.intakeQuestionnaireDetails?.not_attending_school ?? false,
  );
  const childBehaviorIssues = childInfo?.child_behavior_issues ?? '';
  const savedValueRef = useRef(childBehaviorIssues);

  useEffect(() => {
    savedValueRef.current = childBehaviorIssues;
  }, [childBehaviorIssues]);

  const save = useCallback(
    async (value: string) => {
      if (!intakeQuestionnaireId || value === savedValueRef.current) return;
      const result = await dispatch(
        intakeQuestionnaireActions.updateIntakeChildInfo({
          intakeQuestionnaireId,
          data: { child_behavior_issues: value },
          getAccessTokenSilently,
        }),
      );
      if (intakeQuestionnaireActions.updateIntakeChildInfo.fulfilled.match(result)) {
        savedValueRef.current = value;
      }
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  return {
    childBehaviorIssues,
    save,
    extraRequirementMet:
      isChildBackgroundComplete(childInfo) && !hasChildScheduleErrors(childInfo, notAttendingSchool),
    hasNonFormikSavedValues: hasChildFieldValue(childInfo),
  };
}
